import {
  addAiAttachment,
  addAiLink,
  addIdeaAttachment,
  addIdeaLink,
  hasUploadingAttachments,
  handleAiAttachment,
  handleIdeaAttachment,
  submitAiTopic,
  submitIdea,
  validateAiSources,
  validateSourceInputs,
} from "../prototypeActions";
import { matchCall, inputValue, textareaValue } from "../domUtils";
import type { PrototypeState } from "../controllerState";
import type { Root } from "../prototypeTypes";
import { setWizardStep } from "../formProfileController";
import { closeModal, openModal } from "../navigationController";
import { openPaywallWithLayout } from "../commerceController";
import { checkClientIntent } from "../authGuards";
import { recordRuntimeError } from "../prototypeRuntimeSupport";
import { clearOperationFeedback, paintOperationFeedback } from '../operationFeedback';
import { hydrateCommunityView } from "../viewHydrator";
import { showFieldError, clearFieldError, clearPanelErrors, focusFirstFieldError, validateIdeaSubtitle, validateSubtitle } from "../wizardValidation";
import { lockSubmitButton, unlockSubmitButton, renderAiCreateTurnstile, resetAiCreateTurnstile, renderIdeaTurnstile, resetIdeaTurnstile } from "../turnstileWidgetController";
import { handleFeedbackAttachmentChange } from './feedbackAttachmentChangeHandler';
import { closeAiChapterLowRange, confirmAiChapterLowRange, invalidateAiCreateLaunch, launchAiCreate } from '../aiCreateLaunchGate';
import { SOURCE_INPUT_REQUIRED_ERROR } from '../../../domain/sourceAttachmentPolicy';
import { reconcileVisibleWizardTurnstile } from '../modalTurnstileLifecycle';

/**
 * Resolve which DOM container holds the Community list the user is currently
 * looking at. On PC the active view is #view-community; on mobile it's the
 * Social sub-panel inside #view-social. Named mounts keep DOM/state local;
 * hydration resolves the mount from this active outer scope.
 */
function resolveCommunityScope(root: Root): HTMLElement | undefined {
  const pcView = root.querySelector<HTMLElement>("#view-community.view.active");
  if (pcView) return pcView;
  const socialPanel = root.querySelector<HTMLElement>('#view-social .social-panel[data-sub-panel="community"]');
  return socialPanel ?? undefined;
}

/**
 * Open the Submit Idea modal only when the viewer is allowed to submit
 * one. P4.1 后所有登录用户（含 Free）都能提交 Idea，visitor 走 login modal。
 */
function openSubmitIdeaOrPaywall(root: Root, state: PrototypeState) {
  // P4.1: Free/Lite/Pro/Max 已登录即可提交 Idea
  // 仅 visitor 需要登录
  const decision = checkClientIntent(state, "submitIdea");
  if (decision.allowed) {
    openModal(root, "submitIdeaModal");
	void reconcileVisibleWizardTurnstile(root, state.runtime);
    return;
  }
  if (decision.reason === "login") {
    openModal(root, "loginModal");
    return;
  }
  // upgrade 分支：P4.1 后 submitIdea 门槛降为 free，理论上已登录用户不会进入此分支
  const note = root.querySelector<HTMLElement>("#paywallIntentNote");
  if (note) {
    note.textContent = "Sign in to propose a community topic and watch it become a structured course.";
    note.style.display = "";
  }
  const target = root.querySelector<HTMLElement>("#paywallTargetPlan");
  if (target) target.textContent = decision.requiredPlan;
  openPaywallWithLayout(root, state);
}

/**
 * Opens a source-file picker while the trusted click that initiated it is
 * still active. WebKit drops that activation once an action route crosses an
 * async boundary, even when the eventual `input.click()` is otherwise valid.
 *
 * The delegated event binding has already completed the synchronous access
 * decision before calling this. Keep the picker dispatch ahead of the normal
 * async action router; file uploads themselves remain authenticated and
 * validated by their existing change handlers and server endpoints.
 */
export function activateSourceAttachmentPicker(normalized: string, root: Root): boolean {
  if (normalized.includes('addIdeaAttachment')) {
    addIdeaAttachment(root);
    return true;
  }
  if (normalized.includes('addAiAttachment')) {
    addAiAttachment(root);
    return true;
  }
  return false;
}

export async function routeIdeaAndAiActions(normalized: string, root: Root, state: PrototypeState, source?: HTMLElement) {
  if (normalized.includes("showSubmitIdea")) {
    openSubmitIdeaOrPaywall(root, state);
  }
  if (normalized.includes("closeSubmitIdea")) {
    resetIdeaTurnstile(root);
    closeModal(root, "submitIdeaModal");
  }
  if (normalized.includes("addIdeaLink")) {
    addIdeaLink(root);
    return true;
  }
  if (normalized.includes("addAiLink")) {
    addAiLink(root);
    return true;
  }
  if (activateSourceAttachmentPicker(normalized, root)) return true;
  const ideaWizardStep = matchCall(normalized, "wizardNext");
  if (ideaWizardStep) {
    const target = Number(ideaWizardStep);
    const current = target < 2 ? 1 : target > 2 ? 3 : 2; // determine source step
    // Forward navigation (Continue): validate current step before advancing.
    // Back navigation: just clear errors and switch.
    if (target > 1) {
      // Validating step before moving forward
      if (target === 2) {
        // Step 1 -> 2: validate title (required) + subtitle (optional but validated if filled).
        // Collect all errors before returning -- don't short-circuit on the first one.
        let hasStep1Error = false;
        const title = inputValue(root, "ideaTitle").trim();
        if (title.length === 0) {
          showFieldError(root, "ideaTitle", "Please enter a topic title with at least 8 characters.");
          hasStep1Error = true;
        } else if (title.length < 8) {
          showFieldError(root, "ideaTitle", "Topic title must be at least 8 characters.");
          hasStep1Error = true;
        } else if (title.length > 120) {
          showFieldError(root, "ideaTitle", "Topic title must not exceed 120 characters.");
          hasStep1Error = true;
        } else {
          clearFieldError(root, "ideaTitle");
        }
        const subtitle = inputValue(root, "ideaSubtitle").trim();
		const subtitleError = validateIdeaSubtitle(subtitle);
        if (subtitleError) {
          showFieldError(root, "ideaSubtitle", subtitleError);
          hasStep1Error = true;
        } else {
          clearFieldError(root, "ideaSubtitle");
        }
        if (hasStep1Error) {
			focusFirstFieldError(root, ['ideaTitle', 'ideaSubtitle']);
			return true;
		}
      }
      if (target === 3) {
        // Block navigation if attachments are still uploading.
        if (hasUploadingAttachments(root, "ideaAttachmentList")) {
          clearFieldError(root, "ideaLinkList");
          showFieldError(root, "ideaAttachmentList", "Attachments are still uploading. Please wait for them to finish.");
		  focusFirstFieldError(root, ['ideaAttachmentList']);
          return true;
        }
        clearFieldError(root, "ideaAttachmentList");
        // Step 2 -> 3: validate completed source input after uploads settle.
        const linkError = validateSourceInputs(root, 'ideaLinkList', 'ideaAttachmentList');
        if (linkError) {
          showFieldError(root, "ideaLinkList", linkError);
		  if (linkError !== SOURCE_INPUT_REQUIRED_ERROR) focusFirstFieldError(root, ['ideaLinkList']);
          return true;
        }
        clearFieldError(root, "ideaLinkList");
      }
    }
    // Clear errors on the panel we're navigating away from
    clearPanelErrors(root, `wizardPanel${current}`);
    setWizardStep(root, "wizard", target);
    // When entering Step 3, render the visible Turnstile widget and lock
    // the submit button until the user solves the human verification.
    // Mirrors the AI Create wizard's Turnstile integration exactly.
    if (target === 3) {
      lockSubmitButton(root, "ideaSubmitBtn");
      void renderIdeaTurnstile(root, state.runtime, {
        onSuccess: () => unlockSubmitButton(root, "ideaSubmitBtn"),
        onError: () => lockSubmitButton(root, "ideaSubmitBtn"),
        onExpired: () => lockSubmitButton(root, "ideaSubmitBtn"),
      });
    } else {
      // Leaving step 3: clean up the widget so it re-renders fresh next time.
      resetIdeaTurnstile(root);
    }
    return true;
  }
  if (normalized.includes("submitIdea")) {
    // Belt-and-braces: even if the modal opened, re-check the plan here
    // so a manual DOM edit or replay cannot bypass the gate.
    const decision = checkClientIntent(state, "submitIdea");
    if (!decision.allowed) {
      if (decision.reason === "login") {
        openModal(root, "loginModal");
        return true;
      }
      const target = root.querySelector<HTMLElement>("#paywallTargetPlan");
      if (target) target.textContent = decision.requiredPlan;
      resetIdeaTurnstile(root);
      closeModal(root, "submitIdeaModal");
      openPaywallWithLayout(root, state);
      return true;
    }
	clearOperationFeedback(root, 'ideaFormAlert');
    const submitted = await submitIdea(root, state.runtime, {
      closeModal, openModal,
      failValidation: (message) => paintOperationFeedback(root, 'ideaFormAlert', message),
      notifyPreservedDraftSuccess: () => paintOperationFeedback(root, 'ideaFormAlert', 'Your previous submission was successful. Your newer edits are still here.', 'success'),
      recordRuntimeError, currentPlan: state.effectiveAccessPlan, isLoggedIn: state.isLoggedIn,
    });
    if (submitted) await hydrateCommunityView(root, state.runtime, state, { scope: resolveCommunityScope(root) });
    return true;
  }
  if (normalized.includes("closeIdeaSuccess")) {
    closeModal(root, "ideaSuccessModal");
    // Refresh the Community list after successful submission
    await hydrateCommunityView(root, state.runtime, state, { scope: resolveCommunityScope(root) });
  }
  if (normalized.includes("showAiCreateModal")) {
	await launchAiCreate(root, state, source);
	return true;
  }
  if (normalized.includes("closeAiCreate")) {
	invalidateAiCreateLaunch(root);
    resetAiCreateTurnstile(root);
    closeModal(root, "aiCreateModal");
  }
  if (normalized.includes("confirmAiChapterLowRange")) {
    confirmAiChapterLowRange(root);
    return true;
  }
  if (normalized.includes("closeAiChapterLowRange")) {
    closeAiChapterLowRange(root);
    return true;
  }
  const aiWizardStep = matchCall(normalized, "aiWizardNext");
  if (aiWizardStep) {
    const target = Number(aiWizardStep);
    const current = target < 2 ? 1 : target > 2 ? 3 : 2;
    // Forward navigation (Continue): validate current step before advancing.
    // Back navigation: just clear errors and switch.
    if (target > 1) {
      if (target === 2) {
        // Step 1 -> 2: validate title (required) + subtitle (optional but validated if filled).
        // Collect all errors before returning -- don't short-circuit on the first one.
        let hasStep1Error = false;
        const title = inputValue(root, "aiTopicTitle").trim();
        if (title.length === 0) {
          showFieldError(root, "aiTopicTitle", "Please enter a topic title with at least 8 characters.");
          hasStep1Error = true;
        } else if (title.length < 8) {
          showFieldError(root, "aiTopicTitle", "Topic title must be at least 8 characters.");
          hasStep1Error = true;
        } else if (title.length > 120) {
          showFieldError(root, "aiTopicTitle", "Topic title must not exceed 120 characters.");
          hasStep1Error = true;
        } else {
          clearFieldError(root, "aiTopicTitle");
        }
        const subtitle = inputValue(root, "aiTopicSubtitle").trim();
        const subtitleError = validateSubtitle(subtitle);
        if (subtitleError) {
          showFieldError(root, "aiTopicSubtitle", subtitleError);
          hasStep1Error = true;
        } else {
          clearFieldError(root, "aiTopicSubtitle");
        }
        if (hasStep1Error) {
			focusFirstFieldError(root, ['aiTopicTitle', 'aiTopicSubtitle']);
			return true;
		}
      }
      if (target === 3) {
        // Block navigation if attachments are still uploading.
        if (hasUploadingAttachments(root, "aiAttachmentList")) {
          clearFieldError(root, "aiLinkList");
          showFieldError(root, "aiAttachmentList", "Attachments are still uploading. Please wait for them to finish.");
		  focusFirstFieldError(root, ['aiAttachmentList']);
          return true;
        }
        clearFieldError(root, "aiAttachmentList");
        // Step 2 -> 3: accept a valid link, an uploaded file, or both once uploads settle.
        const sourceError = validateAiSources(root);
        if (sourceError) {
          showFieldError(root, "aiLinkList", sourceError);
		  if (sourceError !== SOURCE_INPUT_REQUIRED_ERROR) focusFirstFieldError(root, ['aiLinkList']);
          return true;
        }
        clearFieldError(root, "aiLinkList");
      }
    }
	// Source feedback belongs to step 2. It must not remain above Details after
	// a successful Continue, even if it was produced before this navigation.
	if (target === 3) clearOperationFeedback(root, 'aiCreateFormAlert');
    clearPanelErrors(root, `aiWizardPanel${current}`);
    setWizardStep(root, "aiWizard", target);
    // When entering Step 3, render the visible Turnstile widget and lock
    // the submit button until the user solves the human verification.
    if (target === 3) {
      lockSubmitButton(root, "aiSubmitBtn");
      void renderAiCreateTurnstile(root, state.runtime, {
        onSuccess: () => unlockSubmitButton(root, "aiSubmitBtn"),
        onError: () => lockSubmitButton(root, "aiSubmitBtn"),
        onExpired: () => lockSubmitButton(root, "aiSubmitBtn"),
      });
    } else {
      // Leaving step 3: clean up the widget so it re-renders fresh next time.
      resetAiCreateTurnstile(root);
    }
    return true;
  }
  if (normalized.includes("submitAiTopic")) {
    // Any logged-in user can submit. Quota is enforced server-side;
    // a 403 ai_quota_exceeded triggers the guidance paywall via handleApiError.
    if (!state.isLoggedIn) {
      openModal(root, "loginModal");
      return true;
    }
	clearOperationFeedback(root, 'aiCreateFormAlert');
    await submitAiTopic(root, state.runtime, state.effectiveAccessPlan, {
      closeModal, openModal,
      failValidation: (message) => paintOperationFeedback(root, 'aiCreateFormAlert', message),
      notifyPreservedDraftSuccess: () => paintOperationFeedback(root, 'aiCreateFormAlert', 'Your previous submission was successful. Your newer edits are still here.', 'success'),
      recordRuntimeError, isLoggedIn: state.isLoggedIn,
    });
    return true;
  }
  if (normalized.includes("closeAiSuccess")) {
    closeModal(root, "aiSuccessModal");
    const { hydrateAiCreateView, currentAiFilter } = await import("../aiCreateViewHydrator");
    await hydrateAiCreateView(state.runtime, root, currentAiFilter(root));
  }
  return false;
}

/**
 * Change-listener handlers — invoked by the data-proto-change listener
 * registered in PrototypeController. Kept here so the handlers and
 * routes live next to the click-driven ones.
 */
export async function runIdeaAndAiChangeHandler(
  action: string,
  event: Event,
  root: Root,
  state: PrototypeState,
) {
  if (action.startsWith("handleIdeaAttachment(")) {
    await handleIdeaAttachment(event, root, state.runtime);
    return;
  }
  if (action.startsWith("handleAiAttachment(")) {
    await handleAiAttachment(event, root, state.runtime);
    return;
  }
  if (action.startsWith("handleFeedbackFile(")) {
    await handleFeedbackAttachmentChange(event, root, state.runtime);
    return;
  }
}
